import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Upload, RotateCcw, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { loadSiteContent } from "../lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["T-Shirts", "Hoodies", "Jackets", "Bottoms", "Accessories"];
const DEPARTMENTS = ["Men", "Women", "Kids"];
const COLLECTIONS = ["Drop 001", "Snake Edition", "Raised Reality", "Essentials"];

const SLOTS = [
    { key: "hero_desktop", label: "Hero — Desktop", fallback: "/assets/HERO_DESKTOP.jpg" },
    { key: "hero_mobile", label: "Hero — Mobile", fallback: "/assets/HERO_MOBILE.jpg" },
    { key: "category_tshirts", label: "Category — T-Shirts", fallback: "/assets/CATEGORY_TSHIRTS.jpg" },
    { key: "category_hoodies", label: "Category — Hoodies", fallback: "/assets/CATEGORY_HOODIES.jpg" },
    { key: "category_jackets", label: "Category — Jackets", fallback: "/assets/CATEGORY_JACKETS.jpg" },
    { key: "category_bottoms", label: "Category — Bottoms", fallback: "/assets/CATEGORY_BOTTOMS.jpg" },
    { key: "editorial", label: "Editorial Campaign", fallback: "/assets/EDITORIAL_CAMPAIGN.jpg" },
];

function apiError(e) {
    const d = e.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
    return e.message || "Something went wrong";
}

function Login({ onLogin }) {
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, form);
            sessionStorage.setItem("voc_admin_token", data.token);
            onLogin(data.token);
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-[#F2F0EA] px-5">
            <form onSubmit={submit} className="w-full max-w-sm">
                <p className="mb-2 text-center text-[9px] uppercase tracking-[0.45em] text-[#8E8D8A]">Vantage Owners Club</p>
                <h1 className="font-display mb-10 text-center text-3xl font-black uppercase tracking-tighter text-[#0A0A0A]">
                    Studio
                </h1>
                <div className="space-y-6">
                    <input
                        data-testid="admin-email-input"
                        type="email"
                        required
                        placeholder="EMAIL"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="voc-input uppercase"
                    />
                    <input
                        data-testid="admin-password-input"
                        type="password"
                        required
                        placeholder="PASSWORD"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="voc-input uppercase"
                    />
                </div>
                {error && (
                    <p data-testid="admin-login-error" className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-red-700">
                        {error}
                    </p>
                )}
                <button
                    data-testid="admin-login-submit"
                    type="submit"
                    disabled={busy}
                    className="mt-8 w-full bg-[#0A0A0A] py-4 font-display text-[11px] font-bold uppercase tracking-[0.35em] text-[#F2F0EA] transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                    {busy ? "Entering" : "Enter Studio"}
                </button>
                <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-[9px] uppercase tracking-[0.3em] text-[#8E8D8A] hover:text-[#0A0A0A]">
                    <ArrowLeft size={11} /> Back to store
                </Link>
            </form>
        </div>
    );
}

function MediaPicker({ token, product }) {
    const [media, setMedia] = useState(null);
    const [sel, setSel] = useState({ front: null, hover: null });
    const [raisedIds, setRaisedIds] = useState([]);
    const [busy, setBusy] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const load = () =>
        axios.get(`${API}/admin/products/media`, { headers, params: { product_id: product.id } }).then(({ data }) => {
            setMedia(data.images);
            setRaisedIds(data.raised_media_ids || []);
            setSel({ front: data.images[0]?.id || null, hover: data.images[1]?.id || null });
        });

    useEffect(() => {
        load().catch((e) => toast.error(apiError(e)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pick = async (role, mediaId) => {
        if (busy) return;
        const next = { ...sel, [role]: mediaId };
        if (role === "front" && next.hover === mediaId) next.hover = sel.front;
        if (role === "hover" && next.front === mediaId) next.front = sel.hover;
        setBusy(true);
        try {
            await axios.post(
                `${API}/admin/products/reorder-media`,
                { product_id: product.id, front_media_id: next.front, hover_media_id: next.hover },
                { headers }
            );
            await load();
            toast.success(role === "front" ? "Main photo set" : "Hover photo set");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    const pickRaised = async (mediaId) => {
        if (busy) return;
        setBusy(true);
        try {
            await axios.post(`${API}/admin/products/raised-image`, { product_id: product.id, media_id: mediaId }, { headers });
            await load();
            toast.success("Raised Reality photo set");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    if (!media) return <p className="py-6 text-center text-[9px] uppercase tracking-[0.35em] text-[#8E8D8A]">Loading photos</p>;
    if (!media.length) return <p className="py-6 text-center text-[9px] uppercase tracking-[0.35em] text-[#8E8D8A]">No photos on this product</p>;

    return (
        <div data-testid={`media-picker-${product.handle}`} className="border-t border-[rgba(10,10,10,0.08)] pt-4">
            <p className="mb-3 text-[8px] uppercase tracking-[0.3em] text-[#8E8D8A]">
                Choose the main photo (1st) and the hover photo
            </p>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-6">
                {media.map((m) => (
                    <div key={m.id} className="relative">
                        <div
                            className={`aspect-[4/5] overflow-hidden border bg-[#ECEAE4] ${
                                sel.front === m.id ? "border-[#0A0A0A]" : sel.hover === m.id ? "border-dashed border-[#6B6A66]" : "border-[rgba(10,10,10,0.12)]"
                            }`}
                        >
                            <img src={m.url} alt="" className="h-full w-full object-cover" />
                        </div>
                        {sel.front === m.id && (
                            <span className="absolute left-1 top-1 bg-[#0A0A0A] px-1.5 py-0.5 text-[7px] uppercase tracking-[0.2em] text-[#F2F0EA]">1st</span>
                        )}
                        {sel.hover === m.id && (
                            <span className="absolute right-1 top-1 border border-[#0A0A0A] bg-white px-1.5 py-0.5 text-[7px] uppercase tracking-[0.2em] text-[#0A0A0A]">Hover</span>
                        )}
                        {raisedIds.includes(m.id) && (
                            <span className="absolute bottom-1 left-1 bg-[#8E8D8A] px-1.5 py-0.5 text-[7px] uppercase tracking-[0.2em] text-[#F2F0EA]">Raised</span>
                        )}
                        <div className="mt-1.5 flex gap-1">
                            <button
                                data-testid={`media-front-${m.id.split("/").pop()}`}
                                onClick={() => pick("front", m.id)}
                                disabled={busy || sel.front === m.id}
                                className="flex-1 bg-[#0A0A0A] py-1.5 text-[7px] font-bold uppercase tracking-[0.2em] text-[#F2F0EA] disabled:opacity-30"
                            >
                                1st
                            </button>
                            <button
                                data-testid={`media-hover-${m.id.split("/").pop()}`}
                                onClick={() => pick("hover", m.id)}
                                disabled={busy || sel.hover === m.id || media.length < 2}
                                className="flex-1 border border-[rgba(10,10,10,0.25)] py-1.5 text-[7px] uppercase tracking-[0.2em] text-[#0A0A0A] disabled:opacity-30"
                            >
                                Hover
                            </button>
                            <button
                                data-testid={`media-raised-${m.id.split("/").pop()}`}
                                onClick={() => pickRaised(m.id)}
                                disabled={busy || raisedIds.includes(m.id)}
                                title="Photo shown when customer picks Raised Reality"
                                className="flex-1 border border-[rgba(10,10,10,0.25)] py-1.5 text-[7px] uppercase tracking-[0.2em] text-[#6B6A66] disabled:opacity-30"
                            >
                                Raised
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ProductsTab({ token }) {
    const [products, setProducts] = useState(null);
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(null);
    const [openMediaFor, setOpenMediaFor] = useState(null);
    const [collectionNames, setCollectionNames] = useState(COLLECTIONS);

    const headers = { Authorization: `Bearer ${token}` };
    const load = () =>
        axios.get(`${API}/admin/products`, { headers }).then(({ data }) => {
            setProducts(data);
            const init = {};
            data.forEach((p) => {
                init[p.id] = { product_type: p.product_type || "T-Shirts", departments: p.departments, collection: p.collection || "" };
            });
            setEdits(init);
        });

    useEffect(() => {
        load().catch((e) => toast.error(apiError(e)));
        loadSiteContent().then((d) => {
            if (d.collections?.length) setCollectionNames(d.collections.map((c) => c.name));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setEdit = (id, patch) => setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

    const toggleDept = (id, dept) => {
        const cur = edits[id].departments;
        setEdit(id, { departments: cur.includes(dept) ? cur.filter((d) => d !== dept) : [...cur, dept] });
    };

    const save = async (p) => {
        setSaving(p.id);
        try {
            const e = edits[p.id];
            await axios.post(
                `${API}/admin/products/categorize`,
                { product_id: p.id, product_type: e.product_type, departments: e.departments, collection: e.collection || null },
                { headers }
            );
            toast.success(`Saved — ${p.title}`);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(null);
        }
    };

    if (!products) {
        return <p className="py-20 text-center text-[10px] uppercase tracking-[0.4em] text-[#8E8D8A]">Loading products</p>;
    }

    return (
        <div data-testid="admin-products-tab" className="space-y-3">
            {products.map((p) => {
                const e = edits[p.id];
                if (!e) return null;
                return (
                    <div
                        key={p.id}
                        data-testid={`admin-product-${p.handle}`}
                        className="border border-[rgba(10,10,10,0.1)] bg-white p-4"
                    >
                        <div className="grid grid-cols-1 items-center gap-4 sm:grid-cols-[56px_1fr] lg:grid-cols-[56px_1.2fr_1fr_1fr_1fr_auto]">
                            <div className="h-14 w-14 overflow-hidden bg-[#ECEAE4]">
                                {p.image && <img src={p.image} alt={p.title} className="h-full w-full object-cover" />}
                            </div>
                        <div>
                            <p className="font-display text-[11px] font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">{p.title}</p>
                            <p className="mt-1 text-[8px] uppercase tracking-[0.25em] text-[#8E8D8A]">{p.status}</p>
                        </div>
                        <label className="block">
                            <span className="mb-1 block text-[8px] uppercase tracking-[0.25em] text-[#8E8D8A]">Category</span>
                            <select
                                data-testid={`admin-category-${p.handle}`}
                                value={e.product_type}
                                onChange={(ev) => setEdit(p.id, { product_type: ev.target.value })}
                                className="w-full border border-[rgba(10,10,10,0.2)] bg-transparent px-2 py-2 text-[11px] uppercase tracking-wider"
                            >
                                {CATEGORIES.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </label>
                        <div>
                            <span className="mb-1 block text-[8px] uppercase tracking-[0.25em] text-[#8E8D8A]">Department</span>
                            <div className="flex gap-3">
                                {DEPARTMENTS.map((d) => (
                                    <label key={d} className="flex cursor-pointer items-center gap-1.5 text-[10px] uppercase tracking-wider">
                                        <input
                                            type="checkbox"
                                            data-testid={`admin-dept-${p.handle}-${d.toLowerCase()}`}
                                            checked={e.departments.includes(d)}
                                            onChange={() => toggleDept(p.id, d)}
                                            className="accent-black"
                                        />
                                        {d}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <label className="block">
                            <span className="mb-1 block text-[8px] uppercase tracking-[0.25em] text-[#8E8D8A]">Collection</span>
                            <select
                                data-testid={`admin-collection-${p.handle}`}
                                value={e.collection}
                                onChange={(ev) => setEdit(p.id, { collection: ev.target.value })}
                                className="w-full border border-[rgba(10,10,10,0.2)] bg-transparent px-2 py-2 text-[11px] uppercase tracking-wider"
                            >
                                <option value="">— None —</option>
                                {collectionNames.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </label>
                        <div className="flex gap-2">
                            <button
                                data-testid={`admin-photos-${p.handle}`}
                                onClick={() => setOpenMediaFor(openMediaFor === p.id ? null : p.id)}
                                className={`whitespace-nowrap border px-4 py-2.5 text-[9px] font-bold uppercase tracking-[0.25em] transition-colors ${
                                    openMediaFor === p.id
                                        ? "border-[#0A0A0A] bg-[#0A0A0A] text-[#F2F0EA]"
                                        : "border-[rgba(10,10,10,0.25)] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F2F0EA]"
                                }`}
                            >
                                Photos
                            </button>
                            <button
                                data-testid={`admin-save-${p.handle}`}
                                onClick={() => save(p)}
                                disabled={saving === p.id}
                                className="whitespace-nowrap bg-[#0A0A0A] px-5 py-2.5 text-[9px] font-bold uppercase tracking-[0.25em] text-[#F2F0EA] transition-opacity hover:opacity-85 disabled:opacity-50"
                            >
                                {saving === p.id ? "Saving" : "Save"}
                            </button>
                        </div>
                        </div>
                        {openMediaFor === p.id && <MediaPicker token={token} product={p} />}
                    </div>
                );
            })}
        </div>
    );
}

function collectionSlot(name) {
    return "collection_" + name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function CollectionsTab({ token }) {
    const [collections, setCollections] = useState(null);
    const [images, setImages] = useState({});
    const [newName, setNewName] = useState("");
    const [busy, setBusy] = useState(null);
    const inputs = useRef({});
    const headers = { Authorization: `Bearer ${token}` };

    const refresh = () =>
        loadSiteContent(true).then((d) => {
            setCollections(d.collections || []);
            setImages(d.images || {});
        });
    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const create = async () => {
        const name = newName.trim();
        if (!name) return;
        setBusy("create");
        try {
            await axios.post(`${API}/admin/collections`, { name }, { headers });
            setNewName("");
            await refresh();
            toast.success(`Collection "${name}" created — assign products to it from the Products tab`);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(null);
        }
    };

    const remove = async (name) => {
        if (!window.confirm(`Delete collection "${name}"? It will be removed from every product.`)) return;
        setBusy(name);
        try {
            const { data } = await axios.delete(`${API}/admin/collections/${encodeURIComponent(name)}`, { headers });
            await refresh();
            toast.success(`Deleted — removed from ${data.products_untagged} product${data.products_untagged === 1 ? "" : "s"}`);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(null);
        }
    };

    const upload = async (name, file) => {
        if (!file) return;
        setBusy(name);
        try {
            const blob = await compressImage(file);
            const fd = new FormData();
            fd.append("file", blob, "image.jpg");
            await axios.post(`${API}/admin/site-media/${collectionSlot(name)}`, fd, { headers });
            await refresh();
            toast.success("Collection image updated");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(null);
        }
    };

    const resetImage = async (name) => {
        setBusy(name);
        try {
            await axios.delete(`${API}/admin/site-media/${collectionSlot(name)}`, { headers });
            await refresh();
            toast.success("Back to default image");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(null);
        }
    };

    if (!collections) {
        return <p className="py-20 text-center text-[10px] uppercase tracking-[0.4em] text-[#8E8D8A]">Loading collections</p>;
    }

    return (
        <div data-testid="admin-collections-tab">
            <div className="mb-6 flex flex-wrap items-center gap-3 border border-[rgba(10,10,10,0.1)] bg-white p-4">
                <input
                    data-testid="collection-new-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && create()}
                    placeholder="NEW COLLECTION NAME"
                    className="voc-input max-w-xs flex-1 uppercase"
                />
                <button
                    data-testid="collection-create"
                    onClick={create}
                    disabled={busy === "create" || !newName.trim()}
                    className="bg-[#0A0A0A] px-6 py-3 text-[9px] font-bold uppercase tracking-[0.3em] text-[#F2F0EA] transition-opacity hover:opacity-85 disabled:opacity-50"
                >
                    {busy === "create" ? "Creating" : "Create Collection"}
                </button>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {collections.map((c) => (
                    <div key={c.name} data-testid={`collection-admin-${collectionSlot(c.name)}`} className="border border-[rgba(10,10,10,0.1)] bg-white p-4">
                        <div className="relative aspect-[3/4] overflow-hidden bg-[#ECEAE4]">
                            <img src={c.img} alt={c.name} className="h-full w-full object-cover" />
                            {images[collectionSlot(c.name)] && (
                                <span className="absolute left-2 top-2 bg-[#0A0A0A] px-2 py-1 text-[7px] uppercase tracking-[0.25em] text-[#F2F0EA]">
                                    Custom
                                </span>
                            )}
                        </div>
                        <p className="mt-3 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">{c.name}</p>
                        <p className="mt-0.5 text-[8px] uppercase tracking-[0.25em] text-[#8E8D8A]">{c.meta}</p>
                        <div className="mt-3 flex gap-2">
                            <input
                                ref={(el) => (inputs.current[c.name] = el)}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => upload(c.name, e.target.files?.[0])}
                            />
                            <button
                                data-testid={`collection-image-${collectionSlot(c.name)}`}
                                onClick={() => inputs.current[c.name]?.click()}
                                disabled={busy === c.name}
                                className="flex flex-1 items-center justify-center gap-1.5 bg-[#0A0A0A] px-2 py-2 text-[8px] font-bold uppercase tracking-[0.2em] text-[#F2F0EA] transition-opacity hover:opacity-85 disabled:opacity-50"
                            >
                                <Upload size={10} /> {busy === c.name ? "Working" : "Image"}
                            </button>
                            {images[collectionSlot(c.name)] && (
                                <button
                                    data-testid={`collection-image-reset-${collectionSlot(c.name)}`}
                                    onClick={() => resetImage(c.name)}
                                    disabled={busy === c.name}
                                    aria-label={`Reset ${c.name} image`}
                                    className="border border-[rgba(10,10,10,0.25)] px-2 py-2 text-[#0A0A0A] transition-colors hover:bg-[#0A0A0A] hover:text-[#F2F0EA]"
                                >
                                    <RotateCcw size={11} />
                                </button>
                            )}
                            <button
                                data-testid={`collection-delete-${collectionSlot(c.name)}`}
                                onClick={() => remove(c.name)}
                                disabled={busy === c.name}
                                aria-label={`Delete ${c.name}`}
                                className="border border-[rgba(153,27,27,0.35)] px-2 py-2 text-[#991B1B] transition-colors hover:bg-[#991B1B] hover:text-[#F2F0EA]"
                            >
                                <Trash2 size={11} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <p className="mt-8 text-[9px] uppercase leading-loose tracking-[0.25em] text-[#8E8D8A]">
                Deleting a collection also removes its tag from all products in Shopify. Products keep their other tags.
            </p>
        </div>
    );
}

function HomepageTab({ token }) {
    const [products, setProducts] = useState(null);
    const [picks, setPicks] = useState([]);
    const [saving, setSaving] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        axios.get(`${API}/admin/products`, { headers })
            .then(({ data }) => setProducts(data.filter((p) => p.status === "ACTIVE")))
            .catch((e) => toast.error(apiError(e)));
        loadSiteContent(true).then((d) => setPicks(d.latestDrop || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = (handle) =>
        setPicks((prev) => (prev.includes(handle) ? prev.filter((h) => h !== handle) : [...prev, handle]));

    const save = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/admin/latest-drop`, { handles: picks }, { headers });
            await loadSiteContent(true);
            toast.success("Latest Drop updated — live on the homepage");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!products) {
        return <p className="py-20 text-center text-[10px] uppercase tracking-[0.4em] text-[#8E8D8A]">Loading products</p>;
    }

    return (
        <div data-testid="admin-homepage-tab">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <p className="max-w-md text-[9px] uppercase leading-loose tracking-[0.25em] text-[#8E8D8A]">
                    Tap products to choose the Latest Drop rail. Numbers show the order — the first four appear on the homepage.
                </p>
                <button
                    data-testid="latest-drop-save"
                    onClick={save}
                    disabled={saving}
                    className="bg-[#0A0A0A] px-6 py-3 text-[9px] font-bold uppercase tracking-[0.3em] text-[#F2F0EA] transition-opacity hover:opacity-85 disabled:opacity-50"
                >
                    {saving ? "Saving" : `Save (${picks.length} chosen)`}
                </button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {products.map((p) => {
                    const idx = picks.indexOf(p.handle);
                    const selected = idx !== -1;
                    return (
                        <button
                            key={p.id}
                            data-testid={`latest-drop-pick-${p.handle}`}
                            aria-pressed={selected}
                            onClick={() => toggle(p.handle)}
                            className={`relative border bg-white p-3 text-left transition-all ${
                                selected ? "border-[#0A0A0A] ring-1 ring-[#0A0A0A]" : "border-[rgba(10,10,10,0.1)] hover:border-[rgba(10,10,10,0.35)]"
                            }`}
                        >
                            {selected && (
                                <span className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-[#0A0A0A] text-[10px] font-bold text-[#F2F0EA]">
                                    {idx + 1}
                                </span>
                            )}
                            <div className="aspect-square overflow-hidden bg-[#ECEAE4]">
                                {p.image && <img src={p.image} alt={p.title} className="h-full w-full object-cover" />}
                            </div>
                            <p className="mt-2 text-[9px] font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">{p.title}</p>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

async function compressImage(file) {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1800 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
}

function ImagesTab({ token }) {
    const [map, setMap] = useState({});
    const [busy, setBusy] = useState(null);
    const inputs = useRef({});
    const headers = { Authorization: `Bearer ${token}` };

    const refresh = () => loadSiteContent(true).then((d) => setMap(d.images || {}));
    useEffect(() => {
        refresh();
    }, []);

    const upload = async (slot, file) => {
        if (!file) return;
        setBusy(slot);
        try {
            const blob = await compressImage(file);
            const fd = new FormData();
            fd.append("file", blob, "image.jpg");
            await axios.post(`${API}/admin/site-media/${slot}`, fd, { headers });
            await refresh();
            toast.success("Image updated — live on the site");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(null);
        }
    };

    const reset = async (slot) => {
        setBusy(slot);
        try {
            await axios.delete(`${API}/admin/site-media/${slot}`, { headers });
            await refresh();
            toast.success("Back to default image");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div data-testid="admin-images-tab" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SLOTS.map((s) => (
                <div key={s.key} className="border border-[rgba(10,10,10,0.1)] bg-white p-4">
                    <p className="mb-3 text-[9px] uppercase tracking-[0.3em] text-[#6B6A66]">{s.label}</p>
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#ECEAE4]">
                        <img src={map[s.key] || s.fallback} alt={s.label} className="h-full w-full object-cover" />
                        {map[s.key] && (
                            <span className="absolute left-2 top-2 bg-[#0A0A0A] px-2 py-1 text-[7px] uppercase tracking-[0.25em] text-[#F2F0EA]">
                                Custom
                            </span>
                        )}
                    </div>
                    <div className="mt-3 flex gap-2">
                        <input
                            ref={(el) => (inputs.current[s.key] = el)}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => upload(s.key, e.target.files?.[0])}
                        />
                        <button
                            data-testid={`site-image-upload-${s.key}`}
                            onClick={() => inputs.current[s.key]?.click()}
                            disabled={busy === s.key}
                            className="flex flex-1 items-center justify-center gap-2 bg-[#0A0A0A] px-3 py-2.5 text-[9px] font-bold uppercase tracking-[0.25em] text-[#F2F0EA] transition-opacity hover:opacity-85 disabled:opacity-50"
                        >
                            <Upload size={11} /> {busy === s.key ? "Uploading" : map[s.key] ? "Replace" : "Upload"}
                        </button>
                        {map[s.key] && (
                            <button
                                data-testid={`site-image-reset-${s.key}`}
                                onClick={() => reset(s.key)}
                                disabled={busy === s.key}
                                className="flex items-center gap-2 border border-[rgba(10,10,10,0.25)] px-3 py-2.5 text-[9px] uppercase tracking-[0.25em] text-[#0A0A0A] transition-colors hover:bg-[#0A0A0A] hover:text-[#F2F0EA]"
                            >
                                <RotateCcw size={11} /> Reset
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function AdminPage() {
    const [token, setToken] = useState(() => sessionStorage.getItem("voc_admin_token"));
    const [tab, setTab] = useState("products");

    useEffect(() => {
        document.body.classList.add("voc-native-cursor");
        return () => document.body.classList.remove("voc-native-cursor");
    }, []);

    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => {
                sessionStorage.removeItem("voc_admin_token");
                setToken(null);
            });
    }, [token]);

    if (!token) return <Login onLogin={setToken} />;

    return (
        <div data-testid="admin-panel" className="min-h-screen bg-[#F2F0EA] pb-24">
            <header className="flex items-center justify-between border-b border-[rgba(10,10,10,0.1)] px-5 py-5 sm:px-10">
                <div>
                    <p className="text-[8px] uppercase tracking-[0.4em] text-[#8E8D8A]">Vantage Owners Club</p>
                    <h1 className="font-display text-xl font-black uppercase tracking-tighter text-[#0A0A0A]">Studio</h1>
                </div>
                <div className="flex items-center gap-5">
                    <Link to="/" className="text-[9px] uppercase tracking-[0.3em] text-[#8E8D8A] hover:text-[#0A0A0A]">
                        View store
                    </Link>
                    <button
                        data-testid="admin-logout"
                        onClick={() => {
                            sessionStorage.removeItem("voc_admin_token");
                            setToken(null);
                        }}
                        className="flex items-center gap-2 text-[9px] uppercase tracking-[0.3em] text-[#0A0A0A] hover:opacity-60"
                    >
                        <LogOut size={12} /> Logout
                    </button>
                </div>
            </header>
            <div className="mx-auto max-w-6xl px-5 pt-10 sm:px-10">
                <div className="mb-8 flex gap-6">
                    {[
                        ["products", "Products"],
                        ["homepage", "Latest Drop"],
                        ["collections", "Collections"],
                        ["images", "Site Images"],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            data-testid={`admin-tab-${key}`}
                            onClick={() => setTab(key)}
                            className={`font-display text-[11px] font-bold uppercase tracking-[0.3em] transition-opacity ${
                                tab === key ? "text-[#0A0A0A]" : "text-[#8E8D8A] hover:text-[#0A0A0A]"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {tab === "products" ? (
                    <ProductsTab token={token} />
                ) : tab === "homepage" ? (
                    <HomepageTab token={token} />
                ) : tab === "collections" ? (
                    <CollectionsTab token={token} />
                ) : (
                    <ImagesTab token={token} />
                )}
                <p className="mt-10 text-[9px] uppercase leading-loose tracking-[0.25em] text-[#8E8D8A]">
                    Product videos: upload the clip on the product in Shopify Admin (Media → Add video). The store plays it as a muted loop automatically.
                </p>
            </div>
        </div>
    );
}
